import { Button, cn } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { getUnreadCount } from '../../lib/notifications-api'

/** TanStack Query key for the unread count (shared so mutations can invalidate). */
export const UNREAD_COUNT_KEY = ['notifications', 'unread-count'] as const

/**
 * A bell button with an unread-count badge, intended for the app header. Polls
 * the count on an interval and navigates to the notifications page on click.
 * Fails silently (renders a plain bell) if the count can't be loaded — the
 * indicator must never break the header.
 */
export function UnreadIndicator({ className }: { className?: string }) {
  const navigate = useNavigate()
  const { data } = useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: getUnreadCount,
    // Keep the badge fresh without being chatty.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    // A failed poll should not surface an error; just show no badge.
    retry: false,
  })

  const unread = data?.unread ?? 0
  const label = unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'

  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={label}
      title={label}
      onClick={() => navigate('/app/notifications')}
      className={cn('relative', className)}
    >
      <Bell />
      {unread > 0 && (
        <span
          aria-hidden="true"
          className="absolute -right-0.5 -top-0.5 grid min-w-4 place-items-center rounded-full bg-danger px-1 text-[10px] font-bold leading-4 text-danger-fg"
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Button>
  )
}
